import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ChatbotService, ChatMessage } from './services/chatbot.service';
import { ImisApiService } from './services/imis-api.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  @ViewChild('messageContainer') private messageContainer!: ElementRef;

  title = 'iMIS Staff Assistant';
  messages: ChatMessage[] = [];
  userInput = '';
  isLoading = false;
  userName = '';

  get greeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }
  
  exampleQueries = [
    'List all members at RMIT Melbourne',
    'List all non-members at RMIT Melbourne',
    'Find all members at TestOrg4 with last name Thomas',
    'Add note',
    'Add Site CheckIn'
  ];

  constructor(private chatbot: ChatbotService, private imisApi: ImisApiService) {}

  ngOnInit() {
    const urlId = new URLSearchParams(window.location.search).get('ID');
    if (urlId) {
      this.chatbot.contextMemberId = urlId;
    }

    const contextHint = urlId
      ? `\n\nI can see you're viewing member ID **${urlId}**. You can ask me to add a note for this member directly.`
      : '';

    this.messages.push({
      role: 'assistant',
      content: `Welcome to the iMIS Staff Assistant!\n\nAsk me to query members, add interaction notes, or record site visit check-ins. Try one of the example queries below or ask your own question.${contextHint}`,
      timestamp: new Date()
    });

    firstValueFrom(this.imisApi.authenticate()).then(() => {
      const name = this.imisApi.getLoggedInUserName();
      if (name) {
        this.userName = name;
        this.messages[0].content = `Welcome, ${name}!\n\nAsk me to query members, add interaction notes, or record site visit check-ins. Try one of the example queries below or ask your own question.${contextHint}`;
      }
    }).catch(() => {});
  }

  /**
   * Send a message to the chatbot
   */
  async sendMessage(message?: string) {
    const messageToSend = message || this.userInput.trim();
    
    if (!messageToSend) {
      return;
    }

    // Add user message to UI
    this.messages.push({
      role: 'user',
      content: messageToSend,
      timestamp: new Date()
    });

    this.userInput = '';
    this.isLoading = true;
    this.scrollToBottom();

    try {
      // Get response from chatbot
      const response = await this.chatbot.chat(messageToSend);
      this.messages.push(response);
    } catch (error: any) {
      this.messages.push({
        role: 'assistant',
        content: `Error: ${error.message}`,
        timestamp: new Date()
      });
    } finally {
      this.isLoading = false;
      this.scrollToBottom();
    }
  }

  /**
   * Use an example query
   */
  useExample(query: string) {
    this.sendMessage(query);
  }

  /**
   * Format message content (convert line breaks to HTML)
   */
  formatMessage(content: string): string {
    return content.replace(/\n/g, '<br>');
  }

  /**
   * Reset the conversation
   */
  resetChat() {
    this.chatbot.resetConversation();
    this.chatbot.contextMemberId = null;
    this.messages = [];
    this.ngOnInit();
  }

  /**
   * Scroll to bottom of messages
   */
  private scrollToBottom() {
    setTimeout(() => {
      if (this.messageContainer) {
        this.messageContainer.nativeElement.scrollTop = 
          this.messageContainer.nativeElement.scrollHeight;
      }
    }, 100);
  }

  /**
   * Handle Enter key press
   */
  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Check if there are results to export
   */
  hasResults(): boolean {
    return this.chatbot.lastQueryResults.length > 0;
  }

  /**
   * Export results as CSV
   */
  exportCSV() {
    this.chatbot.exportAsCSV();
  }

}
